import { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import logo from '../assets/images/lubeck logo.webp';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll handle for hash and route changes
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname, location.hash]);

  const navItems = [
    { name: t('navbar.home'), path: '/' },
    { name: t('navbar.about'), path: '/about' },
    {
      name: t('navbar.services'),
      path: '/services',
      submenu: [
        { name: t('services_section.services.0.name1'), path: '/services#steel' },
        { name: t('services_section.services.1.name2'), path: '/services#portable' },
        { name: t('services_section.services.2.name3'), path: '/services#aluminum' },
        { name: t('services_section.services.3.name4'), path: '/services#kitchen' },
        { name: t('services_section.services.4.name5'), path: '/services#advertising' },
        { name: t('services_section.services.5.name6'), path: '/services#event' },
      ]
    },
    { name: t('navbar.projects'), path: '/projects' },
    { name: t('navbar.quote'), path: '/request-quote' },
  ];

  return (
    <nav
      dir='ltr'
      className={`fixed top-0 left-0 right-0 w-screen z-50 bg-transparent transition-all duration-300 ${isScrolled ? 'bg-amber-50 shadow-lg backdrop-blur-md' : ''
        }`}
    >
      <div className='max-w-7xl px-4 md:px-6 lg:px-8 mx-auto'>
        <div className='flex justify-between items-center h-16'>
          {/* Logo */}
          <div className='flex items-center'>
            <div className='flex items-center space-x-3'>
              <img
                src={logo}
                alt="Lubeck Logo"
                className={`h-8 w-8 object-contain ${isScrolled ? '' : 'bg-white '
                  }`}

              />
              <span
                className={`text-xl ${isScrolled ? 'text-black' : 'text-white'
                  } font-semibold tracking-tight`}
              >
                {t('hero.company_name')}
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className='hidden md:flex items-center space-x-1 ml-auto'>
            {navItems.map((item, index) => (
              <div
                key={index}
                className="relative group"
                onMouseEnter={() => item.submenu && setIsServicesOpen(true)}
                onMouseLeave={() => item.submenu && setIsServicesOpen(false)}
              >
                <Link
                  to={item.path}
                  className={`px-4 py-2 text-md font-medium flex items-center space-x-1 ${isScrolled
                    ? 'text-slate-500 hover:text-black'
                    : 'text-white hover:text-amber-600 [text-shadow:0_1px_2px_rgba(0,0,0,0.4)]'
                    } bg-transparent rounded-lg transition-all duration-200 relative group`}
                >
                  <span>{item.name}</span>
                  {item.submenu && (
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180 text-black' : ''}`} />
                  )}
                  <span className='absolute bottom-0 left-1/2 w-0 h-0.5 transition-all duration-200 group-hover:w-8 group-hover:-translate-x-1/2'></span>
                </Link>

                {/* Dropdown Menu */}
                {item.submenu && (
                  <AnimatePresence>
                    {isServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className={`absolute top-full ${isRtl ? 'right-0' : 'left-0'} mt-1 w-max min-w-[260px] bg-black/50 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden z-50 py-2`}
                      >
                        {item.submenu.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            onClick={() => setIsServicesOpen(false)}
                            className="block px-6 py-3 text-sm font-semibold text-white hover:text-black hover:bg-amber-50 transition-colors duration-150 whitespace-nowrap"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className='flex items-center space-x-3'>
            <LanguageSwitcher isScrolled={isScrolled} />

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className='md:hidden p-2 text-black hover:text-white hover:bg-amber-50 rounded-lg transition-colors duration-200'
            >
              {isOpen ? (
                <X className='w-5 h-5' />
              ) : (
                <Menu className='w-5 h-5' />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className='md:hidden overflow-hidden'
            >
              <div className='px-2 pt-2 pb-3 space-y-1 bg-white/90 backdrop-blur-md rounded-lg shadow-lg border border-amber-100 mt-2 mb-4'>
                {navItems.map((item, index) => (
                  <div key={index} className="w-full">
                    {item.submenu ? (
                      <div>
                        <button
                          onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                          className='flex items-center justify-between w-full px-4 py-3 text-base font-medium text-black hover:bg-amber-50 rounded-lg transition-all duration-200'
                        >
                          <span>{item.name}</span>
                          <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {isMobileServicesOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-6 space-y-1"
                            >
                              {item.submenu.map((subItem, subIndex) => (
                                <Link
                                  key={subIndex}
                                  to={subItem.path}
                                  onClick={() => setIsOpen(false)}
                                  className='block w-full text-left px-4 py-2 text-sm font-medium text-slate-600 hover:text-black hover:bg-amber-50 rounded-lg transition-all duration-200'
                                >
                                  {subItem.name}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className='block w-full text-left px-4 py-3 text-base font-medium text-black hover:text-black hover:bg-amber-50 rounded-lg transition-all duration-200'
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
